'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { clsx } from 'clsx/lite'

import { AppUiVisual } from '../app-ui-visual'
import {
  client,
  CURRENT_MONTH_INDEX,
  MONTH_ACTIVITIES,
  MONTH_ACTIVITY_PCT,
  MONTH_PLANNING,
  monthClientPct,
  YEAR_MONTH_LOAD,
  type MonthState,
} from '../demo-data'
import { useVisualCopy, type VisualCopy } from '../visual-copy'
import { AppHeader, AppScreen, AppSubHeader, BoardName, HeaderStat, PlanningPill } from './app-screen'
import styles from './client-row-visual.module.css'
import { useScenario } from './use-scenario'

/** The client whose month the card follows across the board. */
const SUBJECT_NR = '10010'
const SUBJECT_INDEX = MONTH_PLANNING.findIndex((row) => row.nr === SUBJECT_NR)
const SUBJECT_ROW = MONTH_PLANNING[SUBJECT_INDEX]

/** Rows around the focused one, so the card reads as a board rather than a single strip. */
const CONTEXT_BEFORE = 2
const ROWS = MONTH_PLANNING.slice(Math.max(SUBJECT_INDEX - CONTEXT_BEFORE, 0), SUBJECT_INDEX + 3)
const FOCUS_ROW = ROWS.findIndex((row) => row.nr === SUBJECT_NR)

/* Each column carries a short form for the phone-width layout, where the card is 284px across and
   OMZETBELASTING over a 41px column is seven letters and an ellipsis. */
const columnLabel = (t: VisualCopy, key: (typeof MONTH_ACTIVITIES)[number]['key']) => (
  <>
    <span className={styles.wideOnly}>{t.monthActivity[key].label}</span>
    <span className={styles.narrowOnly}>{t.monthActivity[key].short}</span>
  </>
)

/** Resolved in CSS, so a container query can restate it for a phone-width card (see the module). */
const BOARD_GRID = 'var(--cr-cols)'

/* The last activity is the one a phone gives up. Four columns plus a client name in 284px left the
   names at "Kana…" and every heading truncated; three columns is what fits, and the one to drop is
   the last in the reading order. Nothing about the story goes with it: the row still fills left to
   right, and on that beat the badge on the followed row and the counter in the header both move. */
const NARROW_COLUMNS = 3

/** This month's own figures, off the same list the year chart draws, so the two cannot disagree. */
const THIS_MONTH = YEAR_MONTH_LOAD[CURRENT_MONTH_INDEX]

/**
 * The month total without the followed client's finished work, so the counter can be rebuilt from
 * it as that work lands. It ends on the canonical figure rather than near it.
 */
const SETTLED_DONE = SUBJECT_ROW.cells.filter((cell) => cell === 'klaar').length
const BASE_DONE = THIS_MONTH.done - SETTLED_DONE

/**
 * One client's month, filled in left to right.
 *
 * The heading over this card is "de maand van één klant, van links naar rechts", and that reading
 * order is the story: bookkeeping, then the VAT return that follows from it, then payroll, then the
 * wage-tax return that follows that. So the row starts with nothing done and each column lands on
 * its own beat, in the order the work actually happens.
 *
 * The resting frame is still the finished row — `beat === null` renders a complete month, as every
 * visual on the site owes SSR and reduced motion. What the loop adds is the filling; the wrap back
 * to an empty row is one short beat, and it reads as the next month rather than as the finished one
 * being taken apart, because the counter beside it goes back to where that month started.
 */
/**
 * One beat per column that actually changes, between an empty opening and the finished month.
 *
 * Counted rather than written out. A column this client does not have is a dash in every frame, and
 * one whose month ends scheduled starts scheduled too — a beat spent on either renders exactly what
 * the beat before it renders. Derived, that stays true if the client's obligations change.
 */
const FILLABLE_COLUMNS = SUBJECT_ROW.cells.filter((cell) => cell !== 'leeg' && cell !== 'gepland').length

const BEAT_HOLDS: readonly number[] = [
  900, // the client's month, nothing picked up yet
  ...Array.from({ length: FILLABLE_COLUMNS }, () => 850), // one column landing per beat, left to right
  2600, // the month complete — the resting frame, held
]

const REST_BEAT = BEAT_HOLDS.length - 1
const FIRST_FILL_BEAT = 1

/** Resolves `beat` (possibly SSR/reduced-motion `null`). */
function effectiveBeat(beat: number | null): number {
  return beat ?? REST_BEAT
}

/**
 * The focused row's cell at a given beat. A column has landed once the loop has passed its beat;
 * before that it shows as scheduled, which is what the board itself shows for work not yet picked
 * up. Activities this client does not have stay empty throughout — a dash cannot be filled in.
 */
function focusedCell(index: number, beat: number): MonthState {
  const settled = SUBJECT_ROW.cells[index]
  if (settled === 'leeg') return 'leeg'
  return beat >= FIRST_FILL_BEAT + index ? settled : 'gepland'
}

/**
 * Klantrij — one client's month on the Maandplanning board, read left to right: bookkeeping, the
 * VAT return that follows from it, payroll, and the wage-tax return that follows that. The focused
 * row's cells resolve one column per beat and its own completion percentage ticks up beside the
 * client name, exactly where the board shows it. `beat === null` (SSR, no-JS, reduced motion)
 * renders the resolved rest state: the row as the shared month planning has it.
 */
export function ClientRowVisual({ className }: { className?: string }) {
  const { t, fmt } = useVisualCopy()
  const { ref, beat } = useScenario(BEAT_HOLDS, { restBeat: REST_BEAT })
  const effBeat = effectiveBeat(beat)

  const focusedCells = SUBJECT_ROW.cells.map((_, index) =>
    focusedCell(index, effBeat),
  ) as unknown as typeof SUBJECT_ROW.cells
  // The column that landed on this beat, so it can be marked as it arrives.
  const landedIndex = effBeat >= FIRST_FILL_BEAT ? effBeat - FIRST_FILL_BEAT : -1
  const focusedPct = monthClientPct({ ...SUBJECT_ROW, cells: focusedCells })
  // The counters move with the row: each cell that lands finished adds to the month's total, and
  // the column tracks fill as the board arrives.
  const boardLanded = true
  const doneThisMonth = BASE_DONE + focusedCells.filter((cell) => cell === 'klaar').length

  return (
    <AppUiVisual label={t.screen.clientRow.caption} className={className}>
      <AppScreen>
        <div ref={ref} data-visual="client-row" className={clsx(styles.root, 'flex h-full flex-col')}>
          <AppHeader
            title={t.screen.monthPlanning.title}
            detail={t.period.month}
            right={
              <HeaderStat
                value={<span className={styles.counter}>{fmt.number(doneThisMonth)}</span>}
                label={
                  <>
                    {t.word.of} {fmt.number(THIS_MONTH.total)}
                    {/* "1.033 van 1.989 afgerond" beside "Maandplanning / Juli 2026" wants 301px
                        of the 284 a phone gives this card, so both halves were cut: the period
                        fell off the title and the stat wrapped onto the board. The word goes and
                        the figures stay — they are what the counter is for, and the card says
                        Maandplanning either way. */}
                    <span className={styles.wideOnly}> {t.word.completed}</span>
                  </>
                }
              />
            }
          />

          {/* Each activity column carries its own firm-wide completion, as the board's headers do. */}
          <AppSubHeader
            className={styles.board}
            rowLabel={t.word.client}
            columns={MONTH_ACTIVITIES.map((activity, index) => ({
              label: columnLabel(t, activity.key),
              pct: boardLanded ? MONTH_ACTIVITY_PCT[activity.key] : 0,
              className: index >= NARROW_COLUMNS ? styles.hideNarrow : undefined,
            }))}
            style={{ gridTemplateColumns: BOARD_GRID }}
          />

          <div className={clsx(styles.board, 'flex min-h-0 flex-1 flex-col')}>
            {ROWS.map((row, rowIndex) => {
              const focused = rowIndex === FOCUS_ROW
              const cells = focused ? focusedCells : row.cells
              return (
                <div
                  key={row.nr}
                  className={clsx(styles.row, focused && styles.rowFocused, 'grid min-h-0 flex-1 items-center gap-1.5')}
                  style={{ gridTemplateColumns: BOARD_GRID }}
                >
                  <div className="flex min-w-0 items-center gap-1.5 pl-3">
                    <BoardName nr={row.nr} indent={Boolean(client(row.nr).parent)} showTag={false} />
                    {focused && <span className={styles.pctBadge}>{focusedPct}%</span>}
                  </div>
                  {cells.map((state, index) => (
                    <PlanningPill
                      key={MONTH_ACTIVITIES[index].key}
                      tone={state}
                      label={state === 'leeg' ? undefined : (row.assignees[index] ?? undefined)}
                      // Marked on the beat it lands, so the eye follows the row across rather than
                      // having to notice which of four cells changed colour.
                      className={clsx(
                        focused && index === landedIndex && styles.cellLanded,
                        index >= NARROW_COLUMNS && styles.hideNarrow,
                      )}
                    />
                  ))}
                </div>
              )
            })}
          </div>
        </div>
      </AppScreen>
    </AppUiVisual>
  )
}
